import moment from 'moment';

/**
 * Format a sample's collected timestamp (an ISO-8601 string from the lab data,
 * e.g. "2026-02-18T15:08:29Z") into a friendly, readable form — WITHOUT shifting
 * it into the viewer's timezone.
 *
 * The collected time is the source of truth for when the sample was taken, so we
 * display the wall-clock time exactly as recorded. `moment.parseZone` keeps the
 * string's own offset (rather than converting to the browser's local zone), so
 * the output is identical for every viewer regardless of where they are.
 *
 * Non-string / unparseable input passes through untouched.
 */
export function formatCollectedDateTime(value: unknown): unknown {
  if (typeof value !== 'string' || value.trim() === '') return value ?? '';
  const m = moment.parseZone(value);
  return m.isValid() ? m.format('MMM D, YYYY h:mm A') : value;
}

/**
 * Insert spaces before sequential capital-letter words and uppercase the first
 * character. Mirrors the legacy `camelCaseToWords` Angular filter, including
 * its "return non-string input untouched" behavior so it stays a drop-in.
 */
export function camelCaseToWords<T>(items: T): T | string {
  if (typeof items !== 'string') return items;
  return items
    .replace(/[A-Z]{1}[^A-Z ]+/g, (m) => ` ${m}`)
    .replace(/^./, (m) => m.toUpperCase());
}

/**
 * Non-mutating reverse. Mirrors the legacy `reverseArray` Angular filter,
 * including the "return non-array input untouched" behavior.
 */
export function reverseArray<T>(items: T): T extends readonly (infer U)[] ? U[] : T;
export function reverseArray(items: unknown): unknown {
  if (!Array.isArray(items)) return items;
  return items.slice().reverse();
}

/**
 * Lexicographic comparator with numeric-segment awareness — "Sample 2" sorts
 * before "Sample 10". Mirrors the Ruby `naturally` gem the legacy server uses
 * in `Laboratory::Import.generate` when ordering samples.
 *
 * `Intl.Collator` with `numeric: true` is the modern equivalent. We treat
 * null / undefined as empty so they sort consistently (and ahead of any
 * non-empty value, which matches what `Naturally.normalize(nil)` does).
 */
const NATURAL_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});
export function naturalCompare(a: unknown, b: unknown): number {
  const sa = a == null ? '' : String(a);
  const sb = b == null ? '' : String(b);
  return NATURAL_COLLATOR.compare(sa, sb);
}

/**
 * Split a string on whitespace OR underscore, drop case-insensitive duplicate
 * words, and rejoin in title-case. Test-method and matrix strings sometimes
 * come back from the legacy data layer with a category prefix glued on,
 * either space-separated ("MICRO Micro Tape") or underscore-separated
 * ("MICRO_micro Air"); both collapse to a single clean display string.
 *
 * Non-string input passes through untouched.
 */
export function dedupeWords<T>(input: T): T | string;
export function dedupeWords(input: unknown): unknown {
  if (typeof input !== 'string') return input;
  const trimmed = input.trim();
  if (!trimmed) return input;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const word of trimmed.split(/[\s_]+/)) {
    const key = word.toUpperCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  }
  return out.join(' ');
}
